import { Button } from 'app/agriculture/components/Button/Button';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { EmptyState } from 'app/agriculture/components/EmptyState/EmptyState';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { HerdIcon } from 'app/agriculture/icons';
import { AnimalIcon } from 'app/agriculture/icons/components';
import { useCallback, useMemo, useState } from 'react';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { SearchField } from 'app/agriculture/infobox/common/SearchField/SearchField';
import SortableTable from 'app/agriculture/components/Table/SortableTable';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import './HerdListComponent.scss';
import { Paddock } from 'app/agriculture/queries/entities/types/infrastructure/Paddock';
import { FlyToButton } from 'app/agriculture/components/FlyToButton/FlyToButton';
import { useSnapshots } from 'app/agriculture/queries/snapshots/useSnapshots';
import { useEntities } from 'app/agriculture/queries/entities/useEntities';
import { useHerdResults } from 'app/agriculture/routes/Herds/HerdList/components/hooks/useHerdResults';

const PaddockLink = ({ paddock }: { paddock: Paddock | undefined }) => {
    const routeActions = useRouteActions();
    if (paddock?.name === undefined) return <>Offsite</>;

    return (
        <Button
            variant="compact"
            className="HerdList__button"
            text={paddock.name}
            onClick={() => {
                routeActions.goto(paddock.id, { type: 'Paddock' });
            }}
        />
    );
};

export function HerdListComponent() {
    const routeActions = useRouteActions();
    const snapshots = useSnapshots({ snapshotType: 'Herd', shelfLife: null });
    const paddocks = useEntities('Paddock');

    const [disableTableSort, setDisableTableSort] = useState(false);
    const [textFilter, setTextFilter] = useState('');

    const results = useHerdResults(textFilter);

    const handleTextFilterChange = useCallback((textFilter: string) => {
        setTextFilter(textFilter);
        setDisableTableSort(true);
    }, []);

    const handleTableSort = useCallback(() => {
        setDisableTableSort(false);
    }, []);

    const herdData = useMemo(
        () =>
            results.flatMap(({ id, name, animalSpecies, herdType }) => {
                const snapshot = snapshots.get(id) ?? null;
                if (!snapshot) return [];
                const paddock = paddocks.get(snapshot.paddockId ?? '');

                return [
                    {
                        id,
                        name: {
                            value: name,
                            display: (
                                <div className="HerdList__button_container">
                                    {paddock == null ? null : (
                                        <FlyToButton position={paddock.pole.coordinates} />
                                    )}

                                    <Button
                                        variant="compact"
                                        onClick={() => {
                                            routeActions.goto(id, { type: 'Herd' });
                                        }}
                                        text={name}
                                        startIcon={
                                            <AnimalIcon
                                                animalSpecies={animalSpecies}
                                                herdType={herdType}
                                            />
                                        }
                                        className="HerdList__button"
                                    />
                                </div>
                            ),
                        },
                        count: snapshot.totalAnimals,
                        location: {
                            value: !paddock ? 'Offsite' : paddock.name,
                            display: <PaddockLink paddock={paddock} />,
                        },
                    },
                ];
            }),
        [results, paddocks, snapshots, routeActions],
    );

    const totals = herdData.reduce(
        (acc, { id }) => {
            const snapshot = snapshots.get(id) ?? null;
            if (!snapshot) return acc;
            if (snapshot.paddockId === null) {
                acc.offSite += snapshot.totalAnimals;
            } else {
                acc.onSite += snapshot.totalAnimals;
            }
            return acc;
        },
        { onSite: 0, offSite: 0 },
    );

    return (
        <ContentBox title="Herds" Icon={HerdIcon} className="HerdList" variant="xl">
            <ContentBox.Section className="HerdList__header">
                <ContentBox.Section disableDivider>
                    <SimpleGrid columns={3}>
                        <StatDisplay
                            name="Total onsite"
                            value={totals.onSite.toString()}
                            id="TotalOnSite"
                        />
                        <StatDisplay
                            name="Total offsite"
                            value={totals.offSite.toString()}
                            id="TotalOffSite"
                        />
                    </SimpleGrid>
                </ContentBox.Section>
                <ContentBox.Section disableDivider>
                    <SimpleGrid columns={2}>
                        <Button
                            text="Add Herd"
                            startIcon="add"
                            onClick={() => {
                                routeActions.create('Herd');
                            }}
                        />
                        <SearchField textFilter={textFilter} onChange={handleTextFilterChange} />
                    </SimpleGrid>
                </ContentBox.Section>
            </ContentBox.Section>
            <ContentBox.Section scrollable>
                {results.length === 0 && (
                    <EmptyState
                        message={
                            !textFilter
                                ? 'There are currently no herds'
                                : 'Your search returned no results'
                        }
                    />
                )}
                {results.length > 0 && (
                    <SortableTable
                        button={({ id }) => ({
                            text: 'Edit',
                            onClick: () => {
                                routeActions.edit(id, { type: 'Herd' });
                            },
                            startIcon: 'edit',
                            variant: 'compact',
                        })}
                        data={herdData}
                        headCells={[
                            { key: 'name', label: 'Name' },
                            { key: 'count', label: 'Count' },
                            { key: 'location', label: 'Location' },
                        ]}
                        defaultSort={{ key: 'count', order: 'desc' }}
                        disableSort={disableTableSort}
                        onSort={handleTableSort}
                    />
                )}
            </ContentBox.Section>
        </ContentBox>
    );
}
